// src/pages/admin/AdminMedicine.tsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

type PlanKeys = 'single' | 'sub1' | 'sub3' | 'sub6' | 'sub12';
type BillingUnit = 'month' | 'quantity';

/** 数量ベース行の型（価格はモード依存：税抜/税込のいずれかを入力値として保持） */
type QtyPlanRow = {
  id: string;
  quantity_units: string;
  coverage_months_min: string;
  coverage_months_max: string;
  price_val: string;
};

const AdminMedicine: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // === 基本情報 ===
  const [name, setName] = useState('');
  const [type, setType] = useState<'single' | 'set'>('single');
  const [note, setNote] = useState('');
  const [taxRate, setTaxRate] = useState<number>(10);
  const [serviceType, setServiceType] = useState('');

  // === 価格入力モード：'ex'（税抜を入力） or 'in'（税込を入力） ===
  const [priceMode, setPriceMode] = useState<'ex' | 'in'>('in');

  useEffect(() => {
    const saved = localStorage.getItem('admin.medicine.priceMode');
    if (saved === 'ex' || saved === 'in') setPriceMode(saved);
  }, []);

  const changeMode = (m: 'ex' | 'in') => {
    setPriceMode(m);
    localStorage.setItem('admin.medicine.priceMode', m);
  };

  // === 按月価格（5種類）===
  const [prices, setPrices] = useState<Record<PlanKeys, string>>({
    single: '',
    sub1: '',
    sub3: '',
    sub6: '',
    sub12: '',
  });

  // === 计费单位（按月 vs 按数量）===
  const [billingUnits, setBillingUnits] = useState<Record<PlanKeys, BillingUnit>>({
    single: 'quantity',
    sub1: 'month',
    sub3: 'month',
    sub6: 'month',
    sub12: 'month',
  });

  // === 数量ベース行（非表示だが状態は保持） ===
  const [qtyRows, setQtyRows] = useState<QtyPlanRow[]>([]);

  const [submitting, setSubmitting] = useState(false);

  const nf = useMemo(() => new Intl.NumberFormat('ja-JP'), []);

  const serviceOptions = [
    'aga_male',
    'aga_female',
    'obesity_male',
    'obesity_female',
    'ed_premature',
    'pill',
    'skincare',
  ];

  // ===== ユーティリティ =====
  const toHalfWidth = (s: string) =>
    s.replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0));
  const onlyDigits = (s: string) => s.replace(/[^\d]/g, '');
  const parseAmount = (s: string) => {
    const n = Number(onlyDigits(toHalfWidth(s)));
    return Number.isFinite(n) ? n : NaN;
  };

  // ===== 税計算 =====
  const calcIncFromEx = (ex: number) => {
    if (!Number.isFinite(ex)) return NaN;
    const k = 1 + (Number.isFinite(taxRate) ? taxRate : 0) / 100;
    return Math.round(ex * k);
  };
  const calcExFromInc = (inc: number) => {
    if (!Number.isFinite(inc)) return NaN;
    const k = 1 + (Number.isFinite(taxRate) ? taxRate : 0) / 100;
    if (k <= 0) return NaN;
    return Math.round(inc / k);
  };

  // ===== 表示ヘルパ（按月用） =====
  const getMonthlyEx = (key: PlanKeys): number => {
    const raw = parseAmount(prices[key]);
    return priceMode === 'ex' ? raw : calcExFromInc(raw);
  };
  const getMonthlyInc = (key: PlanKeys): number => {
    const raw = parseAmount(prices[key]);
    return priceMode === 'ex' ? calcIncFromEx(raw) : raw;
  };

  // ===== 入力ハンドラ（按月） =====
  const handlePriceChange = (key: PlanKeys, value: string) => {
    const raw = onlyDigits(toHalfWidth(value));
    setPrices((prev) => ({ ...prev, [key]: raw }));
  };

  // ===== プレビュー行（按月） =====
  const previewRows = useMemo(() => {
    const rows: { label: string; key: PlanKeys; ex: string; inc: string }[] = [
      { label: t('admin.medicine.single_purchase') || '単品購入', key: 'single', ex: '', inc: '' },
      { label: '毎月 (定額サブスク)', key: 'sub1', ex: '', inc: '' },
    ];
    return rows.map((r) => {
      const exNum = getMonthlyEx(r.key);
      const incNum = getMonthlyInc(r.key);
      const exStr = Number.isFinite(exNum) && exNum > 0 ? nf.format(exNum) : '-';
      const incStr = Number.isFinite(incNum) && incNum > 0 ? nf.format(incNum) : '-';
      return { ...r, ex: exStr, inc: incStr };
    });
  }, [nf, prices, t, taxRate, priceMode]);

  // ===== 期間マップ（按月） =====
  const subscriptionMap: Record<PlanKeys, number> = {
    single: 0,
    sub1: 1,
    sub3: 3,
    sub6: 6,
    sub12: 12,
  };

  // ===== 数量ベース：行操作（非表示だがロジックは保持） =====
  const removeQtyRow = (id: string) => {
    setQtyRows((prev) => prev.filter((r) => r.id !== id));
  };
  const updateQtyRow = (id: string, patch: Partial<QtyPlanRow>) => {
    setQtyRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  // ===== 保存用の price_plans を構築（按月） =====
  const buildMonthlyPlans = () => {
    const plans: Array<{
      plan_type: 'single_purchase' | 'subscription';
      duration_months: number;
      price_tax_excluded: number;
      price_tax_included: number;
      billing_unit: BillingUnit;
    }> = [];

    (Object.keys(prices) as PlanKeys[]).forEach((key) => {
      const ex = getMonthlyEx(key);
      const inc = getMonthlyInc(key);
      if (!Number.isFinite(ex) || ex <= 0) return;
      if (!Number.isFinite(inc) || inc <= 0) return;

      plans.push({
        plan_type: key === 'single' ? 'single_purchase' : 'subscription',
        duration_months: subscriptionMap[key],
        price_tax_excluded: ex,
        price_tax_included: inc,
        billing_unit: billingUnits[key],
      });
    });

    return plans;
  };

  // ===== 保存用の price_plans を構築（数量） =====
  const buildQuantityPlans = () => {
    const plans: Array<{
      plan_type: 'single_purchase';
      duration_months: number;
      price_tax_excluded: number;
      price_tax_included: number;
      quantity_units: number;
      coverage_months_min?: number | null;
      coverage_months_max?: number | null;
      billing_unit: BillingUnit;
    }> = [];

    qtyRows.forEach((row) => {
      const qty = Number(onlyDigits(toHalfWidth(row.quantity_units)));
      if (!Number.isFinite(qty) || qty <= 0) return;

      const covMin = row.coverage_months_min.trim()
        ? Number(onlyDigits(toHalfWidth(row.coverage_months_min)))
        : NaN;
      const covMax = row.coverage_months_max.trim()
        ? Number(onlyDigits(toHalfWidth(row.coverage_months_max)))
        : NaN;

      const raw = parseAmount(row.price_val);
      const ex = priceMode === 'ex' ? raw : calcExFromInc(raw);
      const inc = priceMode === 'ex' ? calcIncFromEx(raw) : raw;

      if (!Number.isFinite(ex) || ex <= 0) return;
      if (!Number.isFinite(inc) || inc <= 0) return;

      plans.push({
        plan_type: 'single_purchase',
        duration_months: 0,
        price_tax_excluded: ex,
        price_tax_included: inc,
        quantity_units: qty,
        coverage_months_min: Number.isFinite(covMin) ? covMin : null,
        coverage_months_max: Number.isFinite(covMax) ? covMax : null,
        billing_unit: 'quantity',
      });
    });

    return plans;
  };


  // ===== 送信 =====
  const handleSubmit = async () => {
    if (!name.trim() || !serviceType.trim()) {
      alert(t('admin.medicine.required') || '必填项未填写');
      return;
    }

    const monthlyPlans = buildMonthlyPlans();
    const quantityPlans = buildQuantityPlans();
    const price_plans = [...monthlyPlans, ...quantityPlans];

    if (price_plans.length === 0) {
      if (!window.confirm(t('admin.medicine.no_price_confirm') || '尚未设置任何价格，仍要保存吗？')) {
        return;
      }
    }

    try {
      setSubmitting(true);
      await api.post('/admin/medicines', {
        name: name.trim(),
        type,
        note: note.trim(),
        service_type: serviceType,
        price_plans,
      });
      alert(`${t('admin.common.save') || '保存'} ${t('admin.success') || '成功'}`);

      // reset
      setName('');
      setType('single');
      setNote('');
      setServiceType('');
      setPrices({ single: '', sub1: '', sub3: '', sub6: '', sub12: '' });
      setBillingUnits({ single: 'quantity', sub1: 'month', sub3: 'month', sub6: 'month', sub12: 'month' });
      setQtyRows([]);
    } catch (err) {
      console.error('登録失敗', err);
      alert(t('admin.update_failed') || '保存に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  // ===== ショートカット（Ctrl/⌘ + S） =====
  const onKeyDownSubmit: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      handleSubmit();
    }
  };

  // ===== 表示する価格入力行（sub3/sub6/sub12は非表示） =====
  const visiblePriceRows: { label: string; key: PlanKeys }[] = [
    { label: t('admin.medicine.single_purchase') || '単品購入', key: 'single' },
    { label: '毎月 (定額サブスク)', key: 'sub1' },
  ];

  // ===== 画面 =====
  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" onKeyDown={onKeyDownSubmit}>
      {/* === タイトル＆操作 === */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold">{t('admin.edit_medicine') || '薬品の登録/編集'}</h2>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/admin/medicines/list')}
            className="px-4 py-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-800"
          >
            {t('admin.medicine_list') || '薬品一覧'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`px-5 py-2 rounded-full text-white ${submitting ? 'bg-gray-400' : 'bg-coral hover:opacity-90'}`}
            title="Ctrl/⌘ + S"
          >
            {submitting ? (t('processing') || '処理中...') : (t('admin.common.save') || '保存')}
          </button>
        </div>
      </div>

      {/* === 2カラム === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* === 左：フォーム === */}
        <div className="space-y-6">

          {/* 基本情報 */}
          <div className="bg-white rounded-2xl shadow p-5">
            <h3 className="font-semibold text-lg mb-4">{t('admin.medicine.basic') || '基本情報'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('admin.medicine.name') || '名称'}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder={t('admin.medicine.name') || '名称'}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('admin.medicine.type.title') || 'Type'}</label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value as 'single' | 'set')}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="single">{t('admin.medicine.type.single') || '単品'}</option>
                    <option value="set">{t('admin.medicine.type.set') || 'セット'}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('admin.select_service') || 'サービス分類'}</label>
                  <select
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">{t('admin.select_service') || '選択してください'}</option>
                    {serviceOptions.map((key) => (
                      <option key={key} value={key}>
                        {t(`service.${key}`) || key}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('admin.medicine.note') || 'メモ'}</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 min-h-[96px] focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder={t('admin.medicine.note') || 'メモ'}
                />
              </div>
            </div>
          </div>

          {/* 税率 + 価格入力 */}
          <div className="bg-white rounded-2xl shadow p-5">
            <h3 className="font-semibold text-lg mb-4">{t('admin.medicine.tax_and_price') || '税率と価格'}</h3>

            {/* 税率 */}
            <div className="mb-4 flex flex-col gap-2">
              <div>
                <label className="block text-sm text-gray-600 mb-1">{(t('admin.medicine.tax_rate') || '税率')} (%)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={taxRate}
                    onChange={(e) => setTaxRate(Number(e.target.value) || 0)}
                    className="border rounded-lg px-3 py-2 w-28 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                  <div className="flex gap-2">
                    {[0, 8, 10].map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setTaxRate(p)}
                        className={`px-3 py-1.5 rounded-full text-sm border ${taxRate === p ? 'bg-coral/5 border-coral/40 text-coral' : 'bg-gray-50 hover:bg-gray-100'}`}
                      >
                        {p}%
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* 価格入力モード */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('admin.medicine.input_mode') || '入力モード'}</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => changeMode('ex')}
                    className={`px-3 py-1.5 rounded-full text-sm border ${priceMode === 'ex' ? 'bg-coral/5 border-coral/40 text-coral' : 'bg-gray-50 hover:bg-gray-100'}`}
                  >
                    {t('admin.medicine.mode_ex') || '税抜を入力'}
                  </button>
                  <button
                    type="button"
                    onClick={() => changeMode('in')}
                    className={`px-3 py-1.5 rounded-full text-sm border ${priceMode === 'in' ? 'bg-coral/5 border-coral/40 text-coral' : 'bg-gray-50 hover:bg-gray-100'}`}
                  >
                    {t('admin.medicine.mode_in') || '税込を入力'}
                  </button>
                </div>
              </div>
            </div>

            {/* 価格入力行（単品 + 毎月のみ表示） */}
            <h4 className="font-semibold mb-3">{t('admin.medicine.purchase_type') || '購入タイプ'}</h4>
            {visiblePriceRows.map((row) => {
              const exNum = getMonthlyEx(row.key);
              const incNum = getMonthlyInc(row.key);
              const counterpart =
                priceMode === 'ex'
                  ? `${t('admin.medicine.price_in') || '税込'} ${Number.isFinite(incNum) && incNum > 0 ? nf.format(incNum) : '0'} 円`
                  : `${t('admin.medicine.price_ex') || '税抜'} ${Number.isFinite(exNum) && exNum > 0 ? nf.format(exNum) : '0'} 円`;
              return (
                <div key={row.key} className="flex items-center gap-3 py-2 flex-wrap">
                  <label className="w-44 text-sm">{row.label}</label>
                  <div className="relative">
                    <input
                      inputMode="numeric"
                      placeholder={
                        priceMode === 'ex'
                          ? (t('admin.medicine.price_ex') || '税抜き価格')
                          : (t('admin.medicine.price_in') || '税込み価格')
                      }
                      value={prices[row.key]}
                      onChange={(e) => handlePriceChange(row.key, e.target.value)}
                      className="border rounded-lg px-3 py-2 w-44 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    />
                    <span className="absolute right-2 top-2 text-gray-500 text-sm">円</span>
                  </div>
                  <span className="text-gray-500 text-sm">→ {counterpart}</span>

                  {/* 計費単位（単品のみ） */}
                  {row.key === 'single' && (
                    <div className="flex items-center gap-2 ml-4">
                      <label className="text-xs text-gray-600">課金単位:</label>
                      <select
                        value={billingUnits[row.key]}
                        onChange={(e) => setBillingUnits(prev => ({ ...prev, [row.key]: e.target.value as BillingUnit }))}
                        className="border rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      >
                        <option value="quantity">{t('admin.medicine.billing_quantity')}</option>
                        <option value="month">{t('admin.medicine.billing_month')}</option>
                      </select>
                    </div>
                  )}
                </div>
              );
            })}

            {/* sub3/sub6/sub12 は非表示（データは保持） */}
          </div>

        </div>{/* end 左カラム */}

        {/* === 右カラム === */}
        <div className="space-y-6">


          {/* === プレビュー === */}
          <div className="bg-white rounded-2xl shadow-soft p-5 border border-border/50">
            <h3 className="font-semibold text-lg mb-4">{t('admin.preview') || 'プレビュー'}</h3>

            <div className="border rounded-xl overflow-hidden">
              {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
              <div className="md:hidden divide-y">
                {previewRows.map((r, idx) => (
                  <div key={idx} className="p-3 space-y-1">
                    {/* プラン名（カード見出し） */}
                    <div className="font-medium break-words min-w-0">{r.label}</div>
                    {/* 税抜き */}
                    <div className="flex justify-between gap-2 text-sm">
                      <span className="text-gray-500 shrink-0">{t('admin.medicine.price_ex') || '税抜き'}：</span>
                      <span className="min-w-0 text-right break-all">{r.ex === '-' ? '-' : `${r.ex} 円`}</span>
                    </div>
                    {/* 税込み */}
                    <div className="flex justify-between gap-2 text-sm">
                      <span className="text-gray-500 shrink-0">{t('admin.medicine.price_in') || '税込み'}：</span>
                      <span className="min-w-0 text-right break-all">{r.inc === '-' ? '-' : `${r.inc} 円`}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* ▼ デスクトップ(md 以上): テーブル表示 */}
              <table className="hidden md:table w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left px-3 py-2 w-1/2">{t('admin.medicine.plan') || 'プラン'}</th>
                    <th className="text-right px-3 py-2">{t('admin.medicine.price_ex') || '税抜き'}</th>
                    <th className="text-right px-3 py-2">{t('admin.medicine.price_in') || '税込み'}</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2">{r.label}</td>
                      <td className="px-3 py-2 text-right">{r.ex === '-' ? '-' : `${r.ex} 円`}</td>
                      <td className="px-3 py-2 text-right">{r.inc === '-' ? '-' : `${r.inc} 円`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-gray-500 mt-3">
              {t('admin.medicine.preview_hint') || '未入力のプランは保存されません。'}
            </p>
          </div>

        </div>{/* end 右カラム */}

      </div>{/* end grid */}

      {/* === 下部操作バー === */}
      <div className="flex justify-end gap-2 mt-8">
        <button
          onClick={() => navigate('/admin/medicines/list')}
          className="px-6 py-2.5 rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 shadow-soft transition-all"
        >
          {t('admin.medicine_list') || '薬品一覧'}
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className={classNames(
            "px-8 py-2.5 rounded-full text-white font-bold shadow-md transition-all active:scale-95",
            submitting ? 'bg-gray-300' : 'bg-coral hover:shadow-lg hover:-translate-y-0.5'
          )}
          title="Ctrl/⌘ + S"
        >
          {submitting ? (t('processing') || '処理中...') : (t('admin.common.save') || '保存')}
        </button>
      </div>
    </div>
  );
};

export default AdminMedicine;
